import React, { useState } from "react";
import { Funnel, ListChecks, Circle } from "@phosphor-icons/react";
import { fmtDateTime, fmtAgo, humanize } from "./format";

/**
 * Timeline tab — read-only chronological audit feed from case.timeline.
 *
 * Each event source field:
 *   { id, action, author_name, author_role, at, note, meta }
 */
const FILTERS = [
    { key: "all",        label: "All",          test: () => true },
    { key: "status",     label: "Status",       test: (e) => /status|approved|production|shipped|started|completed|cancelled|qc_/.test(e.action) },
    { key: "assignment", label: "Assignments",  test: (e) => /assigned|impersonation/.test(e.action) },
    { key: "payment",    label: "Payments",     test: (e) => /payment|quote/.test(e.action) },
    { key: "note",       label: "Notes",        test: (e) => /note|comment/.test(e.action) },
    { key: "file",       label: "Files",        test: (e) => /file|upload|pdf/.test(e.action) },
];

function actionTone(action) {
    if (/approved|completed|shipped|paid|pass/.test(action)) return "emerald";
    if (/fail|revoked|cancelled|reject/.test(action)) return "rose";
    if (/warning|missing|hold/.test(action)) return "amber";
    if (/assigned|started|created|updated/.test(action)) return "primary";
    return "slate";
}
const TONE_DOT = {
    emerald: "bg-emerald-500",
    rose:    "bg-rose-500",
    amber:   "bg-amber-500",
    primary: "bg-primary",
    slate:   "bg-slate-400",
};

export function TimelineTab({ c }) {
    const [filter, setFilter] = useState("all");
    const events = [...(c?.timeline || [])].reverse(); // newest first
    const filtered = events.filter((e) => FILTERS.find((f) => f.key === filter)?.test(e));

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="timeline-tab">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between gap-3">
                <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-2">
                    <ListChecks size={11} /> Audit trail
                </h3>
                <div className="inline-flex rounded-full border border-border bg-card overflow-hidden">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition ${filter === f.key ? "bg-foreground text-white" : "text-muted-foreground hover:bg-secondary"}`}
                            data-testid={`timeline-filter-${f.key}`}
                        >{f.label}</button>
                    ))}
                </div>
            </div>
            <div className="p-5">
                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No events match this filter.</div>
                ) : (
                    <ol className="relative border-l-2 border-border pl-5 space-y-4">
                        {filtered.map((e) => {
                            const tone = actionTone(e.action || "");
                            return (
                                <li key={e.id} className="relative" data-testid={`timeline-event-${e.id}`}>
                                    <div className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full ${TONE_DOT[tone]} border-2 border-card`} />
                                    <div className="flex items-baseline gap-3 flex-wrap">
                                        <span className="text-sm font-semibold">{humanize(e.action)}</span>
                                        <span className="text-xs text-muted-foreground">{fmtAgo(e.at)} · {fmtDateTime(e.at)}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        by <span className="text-foreground font-medium">{e.author_name || "System"}</span>
                                        {e.author_role && <span> · {humanize(e.author_role)}</span>}
                                    </div>
                                    {e.note && (
                                        <div className="mt-1.5 text-sm leading-snug whitespace-pre-wrap p-2 rounded bg-secondary/40 border border-border/50">{e.note}</div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
}
